import { useCallback, useReducer, useRef } from 'react';
import { useConversationTitleListener } from './useConversationTitleListener';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../state/store';
import type { Conversation, Message, MessageUiState } from '../services/api';
import {
  createConversation,
  deleteConversation,
  fetchMessage,
  fetchMessages,
  listConversations,
  patchMessageUiState,
  sendMessage,
  voteMessage as apiVoteMessage,
  MessageVote,
  type ClarificationPayload,
} from '../services/api';
import { usePusherStream, type ThinkingEvent } from './usePusherStream';
import { usePub } from '../../../helpers/hooks/pubSub';

// O8: Local pub-sub event used to deliver the post-turn balance from the
// orchestrator's SSE :done event to the TokenMeter. useTokenUsage subscribes
// and applies the balance optimistically while the full HTTP refresh lands.
const V2_BALANCE_PATCH_EVENT = 'ask_vantage_v2_balance_patch';

export type StreamStatus = 'idle' | 'thinking' | 'streaming' | 'finalizing' | 'done' | 'error';

/** Passed when submitting from a clarification form (persisted `ui_state` + user message). */
export type FollowupMeta = {
  /** Assistant message to PATCH with clarification `ui_state`. */
  assistantMessageId?: string | number;
  clarificationPayload?: ClarificationPayload;
};

export interface ConversationState {
  conversations: Conversation[];
  activeConversationId: number | null;
  messages: Message[];
  streamStatus: StreamStatus;
  thinkingText: string;
  streamingHtml: string;
  streamingMessageId: number | null;
  lastStreamSeq: number | null;
  error: string | null;
  /** Tracks the initial conversations fetch so we never show UI before access is confirmed. */
  conversationsStatus: 'loading' | 'loaded' | 'error';
}

type Action =
  | { type: 'SET_CONVERSATIONS_STATUS'; status: ConversationState['conversationsStatus'] }
  | { type: 'SET_CONVERSATIONS'; conversations: Conversation[] }
  | { type: 'ADD_CONVERSATION'; conversation: Conversation }
  | { type: 'REMOVE_CONVERSATION'; id: number }
  | { type: 'SET_ACTIVE_CONVERSATION'; id: number | null }
  | { type: 'SET_MESSAGES'; messages: Message[] }
  | { type: 'ADD_USER_MESSAGE'; message: Message }
  | { type: 'START_STREAM'; messageId: number }
  | { type: 'THINKING'; text: string; seq?: number }
  | { type: 'APPEND_CHUNK'; html: string; seq?: number }
  | { type: 'FINALIZING'; text?: string }
  | {
      type: 'STREAM_DONE';
      assistantMessageId: number | null;
      fetchedHtml?: string;
      fetchedUiState?: MessageUiState;
      fetchedStreamStats?: Record<string, unknown> | null;
      seq?: number;
    }
  | { type: 'STREAM_ERROR'; error: string }
  | { type: 'UPDATE_CONVERSATION_TITLE'; id: number; title: string }
  | {
      type: 'MERGE_MESSAGE_UI_STATE';
      id: string | number;
      ui_state: import('../services/api').Message['ui_state'];
    }
  | { type: 'UPDATE_MESSAGE_VOTE'; id: string | number; vote: MessageVote }
  | { type: 'RESET' };

const initialState: ConversationState = {
  conversations: [],
  activeConversationId: null,
  messages: [],
  streamStatus: 'idle',
  thinkingText: '',
  streamingHtml: '',
  streamingMessageId: null,
  lastStreamSeq: null,
  error: null,
  conversationsStatus: 'loading',
};

function isStaleSeq(state: ConversationState, seq?: number) {
  if (typeof seq !== 'number') return false;
  return state.lastStreamSeq != null && seq <= state.lastStreamSeq;
}

function reducer(state: ConversationState, action: Action): ConversationState {
  switch (action.type) {
    case 'SET_CONVERSATIONS_STATUS':
      return { ...state, conversationsStatus: action.status };
    case 'SET_CONVERSATIONS':
      return { ...state, conversations: action.conversations };
    case 'ADD_CONVERSATION':
      return {
        ...state,
        conversations: [action.conversation, ...state.conversations],
      };
    case 'REMOVE_CONVERSATION': {
      const filtered = state.conversations.filter((c) => c.id !== action.id);
      return {
        ...state,
        conversations: filtered,
        activeConversationId:
          state.activeConversationId === action.id ? null : state.activeConversationId,
        messages: state.activeConversationId === action.id ? [] : state.messages,
      };
    }
    case 'SET_ACTIVE_CONVERSATION':
      return { ...state, activeConversationId: action.id, messages: [], streamStatus: 'idle' };
    case 'SET_MESSAGES':
      return { ...state, messages: action.messages };
    case 'ADD_USER_MESSAGE':
      return { ...state, messages: [...state.messages, action.message] };
    case 'START_STREAM':
      return {
        ...state,
        streamStatus: 'thinking',
        thinkingText: '',
        streamingHtml: '',
        streamingMessageId: action.messageId,
        lastStreamSeq: null,
        error: null,
      };
    case 'THINKING':
      if (isStaleSeq(state, action.seq)) return state;
      return {
        ...state,
        streamStatus: 'thinking',
        thinkingText: action.text,
        lastStreamSeq: typeof action.seq === 'number' ? action.seq : state.lastStreamSeq,
      };
    case 'APPEND_CHUNK':
      if (isStaleSeq(state, action.seq)) return state;
      return {
        ...state,
        streamStatus: 'streaming',
        thinkingText: state.thinkingText,
        streamingHtml: state.streamingHtml + action.html,
        lastStreamSeq: typeof action.seq === 'number' ? action.seq : state.lastStreamSeq,
      };
    case 'FINALIZING':
      return {
        ...state,
        streamStatus: 'finalizing',
        thinkingText: action.text || 'Finalizing response...',
      };
    case 'STREAM_DONE': {
      if (isStaleSeq(state, action.seq)) return state;
      const htmlContent = action.fetchedHtml || state.streamingHtml;
      const assistantMsg: Message = {
        id: action.assistantMessageId ?? -1,
        message_type: 'assistant',
        content: '',
        html_content: htmlContent,
        status: 'completed',
        response_version: 'v2_html',
        ui_state: action.fetchedUiState ?? {},
        stream_stats: action.fetchedStreamStats ?? null,
        created_at: new Date().toISOString(),
      };
      return {
        ...state,
        streamStatus: 'done',
        messages: [...state.messages, assistantMsg],
        thinkingText: '',
        streamingHtml: '',
        streamingMessageId: null,
        lastStreamSeq: null,
      };
    }
    case 'STREAM_ERROR':
      return {
        ...state,
        streamStatus: 'error',
        error: action.error,
        thinkingText: '',
        streamingHtml: '',
        streamingMessageId: null,
        lastStreamSeq: null,
      };
    case 'UPDATE_CONVERSATION_TITLE':
      return {
        ...state,
        conversations: state.conversations.map((c) =>
          c.id === action.id ? { ...c, title: action.title } : c
        ),
      };
    case 'MERGE_MESSAGE_UI_STATE':
      return {
        ...state,
        messages: state.messages.map((m) =>
          String(m.id) === String(action.id) ? { ...m, ui_state: action.ui_state } : m
        ),
      };
    case 'UPDATE_MESSAGE_VOTE':
      return {
        ...state,
        messages: state.messages.map((m) =>
          String(m.id) === String(action.id) ? { ...m, vote: action.vote } : m
        ),
      };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

export const useConversation = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { startListening, stopListening } = usePusherStream();
  const responseContainerRef = useRef<HTMLDivElement | null>(null);
  const publish = usePub();
  const streamingEnabledFromCompany = useSelector(
    (s: RootState) => (s.company?.company_details as any)?.ask_vantage_v2_streaming_enabled !== false
  );

  const handleTitleUpdated = useCallback((id: number, title: string) => {
    dispatch({ type: 'UPDATE_CONVERSATION_TITLE', id, title });
  }, []);

  useConversationTitleListener(state.activeConversationId, handleTitleUpdated);

  const loadConversations = useCallback(async () => {
    try {
      const conversations = await listConversations();
      dispatch({ type: 'SET_CONVERSATIONS', conversations });
      dispatch({ type: 'SET_CONVERSATIONS_STATUS', status: 'loaded' });
    } catch (err: any) {
      const httpStatus = (err as any)?.response?.status;
      const apiMessage = (err as any)?.response?.data?.message;
      if (httpStatus === 403) {
        dispatch({
          type: 'STREAM_ERROR',
          error: apiMessage || 'You do not have permission to access Ask Vantage. Please contact your administrator.',
        });
        dispatch({ type: 'SET_CONVERSATIONS_STATUS', status: 'error' });
      } else {
        // Non-403: access is fine, conversations list just stays empty
        dispatch({ type: 'SET_CONVERSATIONS_STATUS', status: 'loaded' });
      }
    }
  }, []);

  const startNewConversation = useCallback(async () => {
    const conversation = await createConversation();
    dispatch({ type: 'ADD_CONVERSATION', conversation });
    dispatch({ type: 'SET_ACTIVE_CONVERSATION', id: conversation.id });
    return conversation;
  }, []);

  const selectConversation = useCallback(async (conversationId: number) => {
    dispatch({ type: 'SET_ACTIVE_CONVERSATION', id: conversationId });
    const messages = await fetchMessages(conversationId);
    dispatch({ type: 'SET_MESSAGES', messages });
  }, []);

  const removeConversation = useCallback(
    async (conversationId: number) => {
      await deleteConversation(conversationId);
      dispatch({ type: 'REMOVE_CONVERSATION', id: conversationId });
    },
    []
  );

  const submitMessage = useCallback(
    async (content: string, conversationId?: number) => {
      let convId = conversationId ?? state.activeConversationId;

      if (!convId) {
        const conversation = await createConversation();
        dispatch({ type: 'ADD_CONVERSATION', conversation });
        dispatch({ type: 'SET_ACTIVE_CONVERSATION', id: conversation.id });
        convId = conversation.id;
      }

      const userMsg: Message = {
        id: Date.now(),
        message_type: 'user',
        content,
        html_content: null,
        status: 'pending',
        response_version: 'v1_blocks',
        created_at: new Date().toISOString(),
      };

      dispatch({ type: 'ADD_USER_MESSAGE', message: userMsg });

      const { message_id, pusher_channel, pusher_event, streaming_enabled } = await sendMessage(convId, content);
      const streamingEnabledForRequest =
        typeof streaming_enabled === 'boolean' ? streaming_enabled : streamingEnabledFromCompany;

      dispatch({ type: 'START_STREAM', messageId: message_id });

      startListening(pusher_channel, pusher_event, message_id, {
        onThinking: (event: ThinkingEvent) => {
          dispatch({ type: 'THINKING', text: event.text, seq: event.seq });
        },
        onChunk: (event) => {
          if (!streamingEnabledForRequest) return;

          const htmlFromBlocks = (event.blocks || []).map((b) => b.html).join('');
          const chunkHtml = event.html || htmlFromBlocks;
          if (!chunkHtml) return;

          dispatch({ type: 'APPEND_CHUNK', html: chunkHtml, seq: event.seq });
          if (responseContainerRef.current) {
            responseContainerRef.current.insertAdjacentHTML('beforeend', chunkHtml);
          }
        },
        onDone: async (event) => {
          if (event.balance_remaining != null) {
            publish(V2_BALANCE_PATCH_EVENT, event.balance_remaining);
          }

          const requiresScriptFinalization = Boolean(
            event.script_finalization?.run_scripts_after_render &&
              (event.script_finalization?.script_count || 0) > 0
          );
          const shouldFetchCanonical = Boolean(
            event.message_id &&
              convId &&
              (!streamingEnabledForRequest || event.fetch_required || requiresScriptFinalization)
          );

          if (shouldFetchCanonical && event.message_id && convId) {
            try {
              dispatch({
                type: 'FINALIZING',
                text: requiresScriptFinalization
                  ? 'Finalizing visualizations...'
                  : 'Finalizing response...'
              });
              const msg = await fetchMessage(convId, event.message_id);
              dispatch({
                type: 'STREAM_DONE',
                assistantMessageId: event.message_id,
                fetchedHtml: msg.html_content ?? undefined,
                fetchedUiState: msg.ui_state,
                fetchedStreamStats: msg.stream_stats ?? event.stream_stats ?? null,
                seq: event.seq,
              });
            } catch {
              dispatch({ type: 'STREAM_ERROR', error: 'Failed to load response' });
            }
          } else {
            dispatch({
              type: 'STREAM_DONE',
              assistantMessageId: event.message_id,
              fetchedStreamStats: event.stream_stats ?? null,
              seq: event.seq,
            });
          }
        },
        onError: (err) => {
          dispatch({ type: 'STREAM_ERROR', error: err.message });
        },
      });
    },
    [state.activeConversationId, startListening, streamingEnabledFromCompany]
  );

  const submitFollowup = useCallback(
    async (question: string, opts?: FollowupMeta) => {
      const convId = state.activeConversationId;
      if (
        convId &&
        opts?.assistantMessageId != null &&
        opts.clarificationPayload
      ) {
        try {
          const updated = await patchMessageUiState(convId, opts.assistantMessageId, {
            clarification: {
              submitted: true,
              submitted_at: new Date().toISOString(),
              field_values: opts.clarificationPayload.field_values,
              composed_preview: opts.clarificationPayload.composed_preview
            }
          });
          dispatch({
            type: 'MERGE_MESSAGE_UI_STATE',
            id: opts.assistantMessageId,
            ui_state: updated.ui_state
          });
        } catch (err) {
          console.error('[AskVantage] Failed to persist ui_state', err);
          throw err;
        }
      }
      await submitMessage(question);
    },
    [submitMessage, state.activeConversationId, dispatch]
  );

  const voteMessage = useCallback(
    async (messageId: string | number, vote: MessageVote) => {
      const current = state.messages.find((m) => String(m.id) === String(messageId));
      const newVote = current?.vote === vote ? MessageVote.NEUTRAL : vote;
      // Optimistic update
      dispatch({ type: 'UPDATE_MESSAGE_VOTE', id: messageId, vote: newVote });
      try {
        await apiVoteMessage(messageId, newVote);
      } catch {
        // Revert optimistic update on failure
        dispatch({ type: 'UPDATE_MESSAGE_VOTE', id: messageId, vote: (current?.vote as MessageVote) ?? MessageVote.NEUTRAL });
      }
    },
    [state.messages]
  );

  return {
    state,
    dispatch,
    responseContainerRef,
    loadConversations,
    startNewConversation,
    selectConversation,
    removeConversation,
    submitMessage,
    submitFollowup,
    voteMessage,
    stopListening,
  };
};
